/**
 * Teacher / school insights — DEMO dataset.
 *
 * This is mock data, but it is shaped EXACTLY like the signals we already capture
 * in production, so every view is a faithful "switch-on ready" preview:
 *
 *  - per-question correctness, latency, attempt count and `concept_tags`
 *    (from assessment_answered events / interaction_statements)
 *  - the per-attempt `concept_summary` { conceptTag: {n, ok} }
 *    (from assessment_completed)
 *  - per-distractor misconception tags (the moat, from the assessment spec)
 *  - watch %, replays, completion (from analytics_views)
 *  - first-name-only learner identity + roll (from the SchoolClass / Learner roster)
 *
 * The mastery model mirrors docs/learn-assessment-platform-plan.md:
 *   Mastered / Developing / Emerging / Not-attempted, plus a flagged misconception
 *   when a learner repeatedly falls for the same distractor.
 *
 * When this goes live the API response simply adopts these types and the UI is
 * unchanged. Nothing here calls the network.
 */

// ----------------------------------------------------------------------------
// Types (these become the API contract)
// ----------------------------------------------------------------------------

export type MasteryState = 'mastered' | 'developing' | 'emerging' | 'not-attempted';

export interface Concept {
  /** Namespaced concept tag, e.g. "ncert.xii.phy.current-electricity:series-parallel". */
  tag: string;
  label: string;
  topicId: string;
  /** Per-distractor misconception this concept is prone to, if any. */
  misconceptionTag?: string;
}

export interface Topic {
  id: string;
  title: string;
  subject: string;
  grade: string;
  /** Module share token (links to the interactive video / assessment). */
  shareToken: string;
  conceptTags: string[];
  /** Questions in the topic assessment. */
  totalQuestions: number;
}

export interface Misconception {
  tag: string;
  /** Teacher-facing label, written for the spec (not AI-generated). */
  label: string;
  conceptTag: string;
  /** First-person rephrase for the STUDENT portal ("you read light as speeding up..."). */
  plainStudent: string;
  /** Jargon-free rephrase for the PARENT portal ("he tends to think..."). */
  plainParent: string;
  /** A concrete, 2-minute, no-expertise-needed thing a parent can do at home. */
  atHomeTip: string;
}

export interface QuestionResult {
  questionId: string;
  conceptTag: string;
  isCorrect: boolean;
  attempts: number;
  latencyMs: number;
  /** When wrong, the distractor's misconception tag (if the concept has one). */
  chosenMisconceptionTag?: string;
}

export interface TopicAttempt {
  topicId: string;
  scorePercent: number;
  correct: number;
  total: number;
  durationMs: number;
  passed: boolean;
  /** Days before "today" the attempt was submitted. */
  submittedDaysAgo: number;
  questions: QuestionResult[];
  // engagement (analytics_views)
  watchPercent: number;
  watchTimeMin: number;
  replays: number;
  completedVideo: boolean;
}

export interface ConceptMastery {
  conceptTag: string;
  state: MasteryState;
  correct: number;
  total: number;
  /** Set when this learner repeatedly fell for the concept's misconception. */
  lastMisconceptionTag?: string;
}

export type EngagementLevel = 'high' | 'medium' | 'low';

export interface Learner {
  id: string;
  learnerToken: string;
  firstName: string;
  rollNo: string;
  overallMasteryPercent: number;
  lastActiveDaysAgo: number;
  engagementLevel: EngagementLevel;
  conceptMastery: ConceptMastery[];
  attempts: TopicAttempt[];
}

export interface ClassRoster {
  id: string;
  name: string;
  grade: string;
  subject: string;
  joinCode: string;
  /** Separate, unguessable teacher code (would gate the no-login dashboard in prod). */
  teacherCode: string;
  teacherName: string;
  schoolName: string;
  topics: Topic[];
  concepts: Concept[];
  misconceptions: Misconception[];
  learners: Learner[];
}

export interface NextAction {
  id: string;
  severity: 'high' | 'medium' | 'low';
  kind: 'reteach' | 'practice' | 'enrich' | 'engage';
  title: string;
  /** The evidence behind the suggestion. */
  rationale: string;
  /** The concrete recommended move. */
  action: string;
  /** Class-level: which learners this addresses. */
  affected?: string[];
  targetTopicId?: string;
  stepsToMastery?: number;
  expectedImpact?: string;
}

// ----------------------------------------------------------------------------
// Curriculum definitions (real NCERT Class XII Physics shape)
// ----------------------------------------------------------------------------

const NS = 'ncert.xii.phy';
const QUESTIONS_PER_CONCEPT = 4;

interface MisconceptionSeed { tag: string; label: string; plainStudent: string; plainParent: string; atHomeTip: string }
interface ConceptSeed { id: string; label: string; difficulty: number; misconception?: MisconceptionSeed }
interface TopicSeed { id: string; title: string; shareToken: string; concepts: ConceptSeed[] }

const TOPIC_SEEDS: TopicSeed[] = [
  {
    id: 'electrostatics', title: 'Electrostatics', shareToken: 'electrostatics-xii',
    concepts: [
      { id: 'coulombs-law', label: "Coulomb's Law", difficulty: 0.82 },
      { id: 'electric-field', label: 'Electric Field', difficulty: 0.58, misconception: { tag: 'field-equals-force', label: 'Treats the electric field as the force itself, ignoring the magnitude and sign of the test charge.',
        plainStudent: "You're reading the electric field as the force itself. The field is the force per unit charge, so the actual force also depends on how big the charge is and its sign.",
        plainParent: 'They treat the electric field as if it were the push (force) itself. The field is really the push per unit of charge, so the real force also depends on the size and sign of the charge.',
        atHomeTip: 'Use a hill: the slope (field) is the same everywhere, but a heavier ball (a bigger charge) feels a bigger push. Ask them what changes if the ball is heavier.' } },
      { id: 'potential', label: 'Electric Potential', difficulty: 0.70 },
      { id: 'gauss-law', label: "Gauss's Law", difficulty: 0.48, misconception: { tag: 'flux-depends-shape', label: 'Believes the flux through a closed surface depends on its shape, not only on the charge enclosed.',
        plainStudent: 'You think the flux through a closed surface depends on its shape. It only depends on the charge trapped inside, no matter how you draw the surface.',
        plainParent: 'They think the shape of an imaginary surface changes the answer. It only depends on how much charge is trapped inside it, not the shape.',
        atHomeTip: 'Picture a balloon around a candle: squash it or stretch it and the same amount of light still escapes. The shape does not matter, only what is inside.' } },
    ],
  },
  {
    id: 'current-electricity', title: 'Current Electricity', shareToken: 'current-electricity-xii',
    concepts: [
      { id: 'ohms-law', label: "Ohm's Law", difficulty: 0.85 },
      { id: 'series-parallel', label: 'Series & Parallel', difficulty: 0.30, misconception: { tag: 'adds-parallel-like-series', label: 'Adds parallel resistances the way they would in series, so the combined resistance comes out too large.',
        plainStudent: "You're adding parallel resistors like series ones. In parallel the combined resistance is always smaller than the smallest branch, because the current has more paths.",
        plainParent: 'They add up resistances the wrong way when components sit side by side (in parallel). Side by side, the total is actually smaller, because electricity has more than one path to take.',
        atHomeTip: 'Think of checkout lanes at a shop: open a second lane (parallel) and the queue clears faster, not slower. More paths means less resistance.' } },
      { id: 'kirchhoff', label: "Kirchhoff's Rules", difficulty: 0.55 },
      { id: 'emf-internal', label: 'EMF & Internal Resistance', difficulty: 0.60 },
    ],
  },
  {
    id: 'ray-optics', title: 'Ray Optics', shareToken: 'ray-optics-xii',
    concepts: [
      { id: 'reflection', label: 'Reflection', difficulty: 0.80 },
      { id: 'refraction', label: 'Refraction', difficulty: 0.46, misconception: { tag: 'speeds-up-denser', label: 'Believes light speeds up when it enters a denser medium such as glass or water.',
        plainStudent: 'You read light as speeding up in glass. It actually slows down, and that slowing is exactly what bends the ray.',
        plainParent: 'They tend to think light speeds up inside glass or water. In fact it slows down, and that is precisely what makes the ray bend. This is one of the most common mix-ups in optics, and easy to fix once it is named.',
        atHomeTip: 'Ask them why a straw looks bent in a glass of water, or why a swimming pool looks shallower than it is. Both happen because light slows and bends entering water. A two-minute chat does more than a worksheet here.' } },
      { id: 'lenses', label: 'Lenses', difficulty: 0.50, misconception: { tag: 'convex-sign-on-concave', label: 'Applies the convex-lens sign convention to a concave lens, flipping the image nature.',
        plainStudent: "You're applying the convex-lens sign rule to concave lenses, which flips the image nature. A concave (diverging) lens takes a negative focal length.",
        plainParent: 'They are applying the rule for one kind of lens (convex, like a magnifying glass) to the opposite kind (concave). Seeing the two side by side usually clears it up.',
        atHomeTip: "If you have reading glasses (convex) and someone's distance glasses (concave) at home, let them look through each and describe how the view changes. Seeing the opposite effects side by side is the quickest fix." } },
      { id: 'optical-instruments', label: 'Optical Instruments', difficulty: 0.62 },
    ],
  },
  {
    id: 'em-waves', title: 'Electromagnetic Waves', shareToken: 'em-waves-xii',
    concepts: [
      { id: 'displacement-current', label: 'Displacement Current', difficulty: 0.36, misconception: { tag: 'no-current-capacitor-gap', label: 'Assumes no current can flow across the gap of a capacitor while it charges.',
        plainStudent: 'You assume nothing flows across a capacitor gap while it charges. A changing electric field there acts like a current (displacement current), so the circuit stays continuous.',
        plainParent: 'They assume nothing flows across the small gap inside a capacitor. A changing electric field there behaves like a current, so the circuit still works as one loop.',
        atHomeTip: 'Think of two people passing a ball back and forth across a gap: nothing crosses for good, but there is still a real back-and-forth flow. The changing field plays that role.' } },
      { id: 'em-spectrum', label: 'EM Spectrum', difficulty: 0.70 },
      { id: 'wave-properties', label: 'Wave Properties', difficulty: 0.66 },
      { id: 'wave-production', label: 'Wave Production', difficulty: 0.62 },
    ],
  },
];

const conceptTagOf = (topicId: string, conceptId: string) => `${NS}.${topicId}:${conceptId}`;

export const TOPICS: Topic[] = TOPIC_SEEDS.map((t) => ({
  id: t.id,
  title: t.title,
  subject: 'Physics',
  grade: 'Class XII',
  shareToken: t.shareToken,
  conceptTags: t.concepts.map((c) => conceptTagOf(t.id, c.id)),
  totalQuestions: t.concepts.length * QUESTIONS_PER_CONCEPT,
}));

export const CONCEPTS: Concept[] = TOPIC_SEEDS.flatMap((t) =>
  t.concepts.map((c) => ({
    tag: conceptTagOf(t.id, c.id),
    label: c.label,
    topicId: t.id,
    misconceptionTag: c.misconception?.tag,
  })),
);

export const MISCONCEPTIONS: Misconception[] = TOPIC_SEEDS.flatMap((t) =>
  t.concepts.filter((c) => c.misconception).map((c) => ({
    tag: c.misconception!.tag,
    label: c.misconception!.label,
    conceptTag: conceptTagOf(t.id, c.id),
    plainStudent: c.misconception!.plainStudent,
    plainParent: c.misconception!.plainParent,
    atHomeTip: c.misconception!.atHomeTip,
  })),
);

const CONCEPT_BY_TAG = new Map(CONCEPTS.map((c) => [c.tag, c]));
const TOPIC_BY_ID = new Map(TOPICS.map((t) => [t.id, t]));
const MISC_BY_TAG = new Map(MISCONCEPTIONS.map((m) => [m.tag, m]));
const DIFFICULTY = new Map(
  TOPIC_SEEDS.flatMap((t) => t.concepts.map((c) => [conceptTagOf(t.id, c.id), c.difficulty] as const)),
);
const MISC_HOLD_PROB = new Map(
  TOPIC_SEEDS.flatMap((t) => t.concepts.filter((c) => c.misconception).map((c) =>
    // Engineered class-wide weak spots hold their misconception more stubbornly.
    [c.misconception!.tag, c.difficulty < 0.45 ? 0.85 : 0.5] as const)),
);

export const conceptLabel = (tag: string) => CONCEPT_BY_TAG.get(tag)?.label ?? tag;
export const topicLabel = (id: string) => TOPIC_BY_ID.get(id)?.title ?? id;
export const misconceptionLabel = (tag: string) => MISC_BY_TAG.get(tag)?.label ?? tag;
export const misconceptionByTag = (tag: string): Misconception | undefined => MISC_BY_TAG.get(tag);
export const conceptsOfTopic = (topicId: string) => CONCEPTS.filter((c) => c.topicId === topicId);

// ----------------------------------------------------------------------------
// Generation helpers
// ----------------------------------------------------------------------------

function seeded(seed: string): () => number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => {
    h += 0x6d2b79f5; let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

function masteryState(correct: number, total: number): MasteryState {
  if (total === 0) return 'not-attempted';
  const r = correct / total;
  if (r >= 0.75) return 'mastered';
  if (r >= 0.5) return 'developing';
  return 'emerging';
}

export const MASTERY_RANK: Record<MasteryState, number> = {
  mastered: 3, developing: 2, emerging: 1, 'not-attempted': 0,
};

// Roster: 28 learners with curated ability so the class has a realistic spread
// and clear, teacher-actionable signals (a shared series/parallel misconception,
// a few at-risk learners, a band of high performers ready to be stretched).
const STUDENTS: Array<[string, number]> = [
  ['Aarav', 0.80], ['Priya', 0.78], ['Ishaan', 0.64], ['Ananya', 0.92],
  ['Vihaan', 0.48], ['Diya', 0.60], ['Reyansh', 0.44], ['Saanvi', 0.90],
  ['Arjun', 0.88], ['Aadhya', 0.82], ['Kabir', 0.30], ['Myra', 0.66],
  ['Vivaan', 0.61], ['Anika', 0.77], ['Aryan', 0.50], ['Kiara', 0.76],
  ['Krishna', 0.87], ['Riya', 0.58], ['Shaurya', 0.40], ['Navya', 0.79],
  ['Dhruv', 0.52], ['Sara', 0.74], ['Aditya', 0.86], ['Pari', 0.62],
  ['Rudra', 0.46], ['Avni', 0.89], ['Karthik', 0.81], ['Ira', 0.68],
];

// A handful of lower-ability learners haven't started the latest module yet —
// shows up as a "Not attempted" column, a real and common teacher signal.
const SKIPPED_LAST_TOPIC = new Set(['Kabir', 'Shaurya', 'Rudra']);

function buildLearner(firstName: string, ability: number, index: number): Learner {
  const rand = seeded(`${firstName}:${ability}`);
  const learnerToken = `lt_${seeded(firstName)().toString(36).slice(2, 10)}`;
  const conceptMastery: ConceptMastery[] = [];
  const attempts: TopicAttempt[] = [];

  for (const topic of TOPICS) {
    const skipped = topic.id === 'em-waves' && SKIPPED_LAST_TOPIC.has(firstName);
    if (skipped) {
      for (const tag of topic.conceptTags) {
        conceptMastery.push({ conceptTag: tag, state: 'not-attempted', correct: 0, total: 0 });
      }
      continue;
    }

    const questions: QuestionResult[] = [];
    let topicCorrect = 0;
    let qi = 1;
    for (const tag of topic.conceptTags) {
      const difficulty = DIFFICULTY.get(tag) ?? 0.6;
      const concept = CONCEPT_BY_TAG.get(tag)!;
      const p = clamp01(difficulty + (ability - 0.6) * 0.85 + (rand() - 0.5) * 0.22);
      const total = QUESTIONS_PER_CONCEPT;
      const correct = clamp(Math.round(p * total), 0, total);
      topicCorrect += correct;
      const state = masteryState(correct, total);

      // Misconception: when the learner struggles on a concept that has a known
      // distractor, they may repeatedly fall for it.
      let lastMisconceptionTag: string | undefined;
      if (concept.misconceptionTag && state !== 'mastered') {
        const holdProb = MISC_HOLD_PROB.get(concept.misconceptionTag) ?? 0.5;
        if (rand() < holdProb) lastMisconceptionTag = concept.misconceptionTag;
      }
      conceptMastery.push({ conceptTag: tag, state, correct, total, lastMisconceptionTag });

      // Synthesize per-question detail consistent with the concept score.
      for (let k = 0; k < total; k++) {
        const isCorrect = k < correct;
        questions.push({
          questionId: `q${String(qi).padStart(2, '0')}`,
          conceptTag: tag,
          isCorrect,
          attempts: isCorrect ? (rand() < 0.8 ? 1 : 2) : (rand() < 0.5 ? 2 : 1),
          latencyMs: Math.round((isCorrect ? 7000 : 12000) + rand() * 6000),
          chosenMisconceptionTag: !isCorrect && concept.misconceptionTag && lastMisconceptionTag ? concept.misconceptionTag : undefined,
        });
        qi++;
      }
    }

    const total = topic.totalQuestions;
    const scorePercent = Math.round((topicCorrect / total) * 100);
    const watchPercent = clamp(Math.round(58 + ability * 42 + (rand() - 0.5) * 18), 22, 100);
    const replays = Math.round((1 - ability) * 4 + rand() * 2);
    attempts.push({
      topicId: topic.id,
      scorePercent,
      correct: topicCorrect,
      total,
      durationMs: Math.round((9 + (1 - ability) * 6 + rand() * 4) * 60_000),
      passed: scorePercent >= 60,
      submittedDaysAgo: Math.round(2 + (TOPICS.indexOf(topic) * 4) + (1 - ability) * 5 + rand() * 3),
      questions,
      watchPercent,
      watchTimeMin: Math.round(8 + (1 - ability) * 6 + rand() * 3),
      replays,
      completedVideo: watchPercent >= 90,
    });
  }

  const attempted = conceptMastery.filter((c) => c.state !== 'not-attempted');
  const overallMasteryPercent = attempted.length
    ? Math.round((attempted.reduce((s, c) => s + c.correct, 0) / attempted.reduce((s, c) => s + c.total, 0)) * 100)
    : 0;
  const avgWatch = attempts.length ? attempts.reduce((s, a) => s + a.watchPercent, 0) / attempts.length : 0;
  const engagementLevel: EngagementLevel = avgWatch >= 85 ? 'high' : avgWatch >= 60 ? 'medium' : 'low';
  const lastActiveDaysAgo = SKIPPED_LAST_TOPIC.has(firstName)
    ? Math.round(9 + (1 - ability) * 8 + rand() * 4)
    : Math.round(1 + (1 - ability) * 4 + rand() * 2);

  return {
    id: `learner-${index + 1}`,
    learnerToken,
    firstName,
    rollNo: String(index + 1),
    overallMasteryPercent,
    lastActiveDaysAgo,
    engagementLevel,
    conceptMastery,
    attempts,
  };
}

// ----------------------------------------------------------------------------
// The demo class (single source of truth for every view)
// ----------------------------------------------------------------------------

export const DEMO_CLASS: ClassRoster = {
  id: 'class-xii-a-phys',
  name: 'Class XII-A',
  grade: 'Class XII',
  subject: 'Physics',
  joinCode: 'PHX12A',
  teacherCode: 'tchr_8f3k9q2x',
  teacherName: 'Mrs. Menon',
  schoolName: 'Sunrise Public School',
  topics: TOPICS,
  concepts: CONCEPTS,
  misconceptions: MISCONCEPTIONS,
  learners: STUDENTS.map(([name, ability], i) => buildLearner(name, ability, i)),
};

// ----------------------------------------------------------------------------
// Derived selectors
// ----------------------------------------------------------------------------

export const conceptMasteryOf = (learner: Learner, conceptTag: string): ConceptMastery | undefined =>
  learner.conceptMastery.find((c) => c.conceptTag === conceptTag);

export function topicMasteryOf(learner: Learner, topicId: string): MasteryState {
  const tags = TOPIC_BY_ID.get(topicId)?.conceptTags ?? [];
  const states = tags.map((t) => conceptMasteryOf(learner, t)?.state ?? 'not-attempted');
  if (states.every((s) => s === 'not-attempted')) return 'not-attempted';
  const attempted = states.filter((s) => s !== 'not-attempted');
  const avg = attempted.reduce((s, st) => s + MASTERY_RANK[st], 0) / attempted.length;
  return avg >= 2.5 ? 'mastered' : avg >= 1.5 ? 'developing' : 'emerging';
}

export function attemptOf(learner: Learner, topicId: string): TopicAttempt | undefined {
  return learner.attempts.find((a) => a.topicId === topicId);
}

export interface ClassStats {
  learnerCount: number;
  topicCount: number;
  avgMasteryPercent: number;
  avgScorePercent: number;
  completionPercent: number;
  needingInterventionCount: number;
  flaggedConceptCount: number;
  lastActiveDaysAgo: number;
}

export function needsIntervention(learner: Learner): boolean {
  const emerging = learner.conceptMastery.filter((c) => c.state === 'emerging').length;
  const flagged = learner.conceptMastery.some((c) => c.lastMisconceptionTag);
  return learner.overallMasteryPercent < 55 || emerging >= 3 || (emerging >= 2 && flagged);
}

export function classStats(roster: ClassRoster): ClassStats {
  const ls = roster.learners;
  const avgMastery = ls.reduce((s, l) => s + l.overallMasteryPercent, 0) / ls.length;
  const allAttempts = ls.flatMap((l) => l.attempts);
  const avgScore = allAttempts.reduce((s, a) => s + a.scorePercent, 0) / allAttempts.length;
  const completion = allAttempts.filter((a) => a.completedVideo).length / allAttempts.length;
  const flaggedConcepts = new Set(
    ls.flatMap((l) => l.conceptMastery.filter((c) => c.lastMisconceptionTag).map((c) => c.conceptTag)),
  );
  // A concept counts as a class-wide flag only when many learners share the gap.
  const classFlagged = roster.concepts.filter((c) => {
    const n = ls.filter((l) => conceptMasteryOf(l, c.tag)?.state === 'emerging').length;
    return n >= ls.length * 0.3;
  }).length;
  return {
    learnerCount: ls.length,
    topicCount: roster.topics.length,
    avgMasteryPercent: Math.round(avgMastery),
    avgScorePercent: Math.round(avgScore),
    completionPercent: Math.round(completion * 100),
    needingInterventionCount: ls.filter(needsIntervention).length,
    flaggedConceptCount: classFlagged || flaggedConcepts.size,
    lastActiveDaysAgo: Math.min(...ls.map((l) => l.lastActiveDaysAgo)),
  };
}

export interface ConceptStat {
  concept: Concept;
  mastered: number; developing: number; emerging: number; notAttempted: number;
  avgScorePercent: number;
  misconceptionHolders: number;
}

export function conceptStats(roster: ClassRoster, conceptTag: string): ConceptStat {
  const concept = CONCEPT_BY_TAG.get(conceptTag)!;
  const cells = roster.learners.map((l) => conceptMasteryOf(l, conceptTag)).filter(Boolean) as ConceptMastery[];
  const count = (st: MasteryState) => cells.filter((c) => c.state === st).length;
  const attempted = cells.filter((c) => c.total > 0);
  const avgScore = attempted.length
    ? Math.round((attempted.reduce((s, c) => s + c.correct, 0) / attempted.reduce((s, c) => s + c.total, 0)) * 100)
    : 0;
  return {
    concept,
    mastered: count('mastered'), developing: count('developing'),
    emerging: count('emerging'), notAttempted: count('not-attempted'),
    avgScorePercent: avgScore,
    misconceptionHolders: cells.filter((c) => c.lastMisconceptionTag).length,
  };
}

export interface TopicStat {
  topic: Topic;
  mastered: number; developing: number; emerging: number; notAttempted: number;
  avgScorePercent: number;
  dominantMisconception?: { tag: string; holders: number };
}

export function topicStats(roster: ClassRoster, topicId: string): TopicStat {
  const topic = TOPIC_BY_ID.get(topicId)!;
  const states = roster.learners.map((l) => topicMasteryOf(l, topicId));
  const count = (st: MasteryState) => states.filter((s) => s === st).length;
  const attempts = roster.learners.map((l) => attemptOf(l, topicId)).filter(Boolean) as TopicAttempt[];
  const avgScore = attempts.length ? Math.round(attempts.reduce((s, a) => s + a.scorePercent, 0) / attempts.length) : 0;

  let dominant: { tag: string; holders: number } | undefined;
  for (const tag of topic.conceptTags) {
    const concept = CONCEPT_BY_TAG.get(tag);
    if (!concept?.misconceptionTag) continue;
    const holders = roster.learners.filter((l) => conceptMasteryOf(l, tag)?.lastMisconceptionTag).length;
    if (holders > 0 && (!dominant || holders > dominant.holders)) dominant = { tag: concept.misconceptionTag, holders };
  }
  return {
    topic,
    mastered: count('mastered'), developing: count('developing'),
    emerging: count('emerging'), notAttempted: count('not-attempted'),
    avgScorePercent: avgScore,
    dominantMisconception: dominant,
  };
}

// ----------------------------------------------------------------------------
// Item analysis (per topic, per question)
// ----------------------------------------------------------------------------

export interface ItemStat {
  conceptTag: string;
  conceptLabel: string;
  questionId: string;
  correctPercent: number;
  avgLatencyMs: number;
  misconceptionTag?: string;
  misconceptionPercent: number; // share of wrong answers that chose the misconception distractor
}

export function itemStats(roster: ClassRoster, topicId: string): ItemStat[] {
  const topic = TOPIC_BY_ID.get(topicId)!;
  // Aggregate the synthesized per-question rows across learners by question id.
  const byQ = new Map<string, QuestionResult[]>();
  for (const l of roster.learners) {
    const a = attemptOf(l, topicId);
    if (!a) continue;
    for (const q of a.questions) {
      if (!byQ.has(q.questionId)) byQ.set(q.questionId, []);
      byQ.get(q.questionId)!.push(q);
    }
  }
  const rows: ItemStat[] = [];
  for (const tag of topic.conceptTags) {
    const concept = CONCEPT_BY_TAG.get(tag)!;
    const qs = Array.from(byQ.entries())
      .map(([qid, list]) => ({ qid, list: list.filter((q) => q.conceptTag === tag) }))
      .filter((x) => x.list.length > 0);
    for (const { qid, list } of qs) {
      const correct = list.filter((q) => q.isCorrect).length;
      const wrong = list.filter((q) => !q.isCorrect);
      const misc = wrong.filter((q) => q.chosenMisconceptionTag).length;
      rows.push({
        conceptTag: tag,
        conceptLabel: concept.label,
        questionId: qid,
        correctPercent: Math.round((correct / list.length) * 100),
        avgLatencyMs: Math.round(list.reduce((s, q) => s + q.latencyMs, 0) / list.length),
        misconceptionTag: concept.misconceptionTag,
        misconceptionPercent: wrong.length ? Math.round((misc / wrong.length) * 100) : 0,
      });
    }
  }
  return rows;
}

// ----------------------------------------------------------------------------
// Next best actions — written as if AI-generated (deterministic, evidence-led).
// No em dashes in copy. Pre-written for the demo; in production these come from
// the backend Claude model over the same facts.
// ----------------------------------------------------------------------------

export function classActions(roster: ClassRoster): NextAction[] {
  const ls = roster.learners;
  const n = ls.length;
  const actions: NextAction[] = [];

  // 1) Reteach the concepts with the largest shared misconception clusters.
  const misconceptionConcepts = roster.concepts
    .filter((c) => c.misconceptionTag)
    .map((c) => {
      const holders = ls.filter((l) => conceptMasteryOf(l, c.tag)?.lastMisconceptionTag);
      const wrong = ls.filter((l) => {
        const m = conceptMasteryOf(l, c.tag);
        return m && m.total > 0 && m.correct / m.total < 0.5;
      });
      return { concept: c, holders, wrong };
    })
    .filter((x) => x.holders.length >= Math.ceil(n * 0.25))
    .sort((a, b) => b.holders.length - a.holders.length);

  for (const { concept, holders, wrong } of misconceptionConcepts.slice(0, 2)) {
    const topic = TOPIC_BY_ID.get(concept.topicId)!;
    const miscLabel = misconceptionLabel(concept.misconceptionTag!).replace(/\.$/, '');
    actions.push({
      id: `class-reteach-${concept.tag}`,
      severity: holders.length >= n * 0.4 ? 'high' : 'medium',
      kind: 'reteach',
      title: `Re-teach ${concept.label}`,
      rationale: `${wrong.length} of ${n} learners scored below half on ${concept.label}, and ${holders.length} of them share the same misconception: ${miscLabel.toLowerCase()}.`,
      action: `Run a focused 12 minute reteach that confronts this specific misconception, then assign a 5 item practice set on ${concept.label} and re-test. The interactive module "${topic.title}" isolates exactly this step, so it can be reassigned as the warm up.`,
      affected: holders.map((l) => l.firstName),
      targetTopicId: topic.id,
      expectedImpact: `Projected to move ${Math.max(2, Math.round(holders.length * 0.6))} learners from Emerging to Developing on ${concept.label}.`,
    });
  }

  // 2) Whole-class practice on the lowest-scoring attempted concept w/o a named misconception.
  const weakest = roster.concepts
    .filter((c) => !c.misconceptionTag)
    .map((c) => ({ c, stat: conceptStats(roster, c.tag) }))
    .filter((x) => x.stat.emerging + x.stat.developing >= n * 0.4)
    .sort((a, b) => a.stat.avgScorePercent - b.stat.avgScorePercent)[0];
  if (weakest) {
    const topic = TOPIC_BY_ID.get(weakest.c.topicId)!;
    actions.push({
      id: `class-practice-${weakest.c.tag}`,
      severity: 'medium',
      kind: 'practice',
      title: `Build fluency on ${weakest.c.label}`,
      rationale: `Class average on ${weakest.c.label} is ${weakest.stat.avgScorePercent}%, the lowest in ${topic.title}, with ${weakest.stat.emerging} learners still Emerging.`,
      action: `Set a short ${weakest.c.label} drill as homework and review the two hardest items at the start of the next class. No reteach needed, the gap looks like practice volume rather than a misconception.`,
      targetTopicId: topic.id,
      expectedImpact: `A single practice cycle typically lifts class average here by 8 to 12 points.`,
    });
  }

  // 3) Stretch the learners who have mastered most of the syllabus.
  const ready = ls.filter((l) => l.conceptMastery.filter((c) => c.state === 'mastered').length >= roster.concepts.length * 0.7);
  if (ready.length >= 3) {
    actions.push({
      id: 'class-enrich',
      severity: 'low',
      kind: 'enrich',
      title: 'Stretch the top performers',
      rationale: `${ready.length} learners have mastered at least 70% of the concepts covered and are coasting.`,
      action: `Give this group a challenge set (numerical problems combining two topics, for example a charged particle moving through both a field and a resistor network) so they keep progressing while you run the reteach for the rest.`,
      affected: ready.map((l) => l.firstName),
      expectedImpact: 'Keeps high performers engaged and frees you to focus class time on the intervention group.',
    });
  }

  // 4) Re-engage the learners who have gone quiet / not started the latest module.
  const quiet = ls.filter((l) => l.engagementLevel === 'low' || l.lastActiveDaysAgo >= 8);
  if (quiet.length >= 2) {
    actions.push({
      id: 'class-engage',
      severity: quiet.some((l) => needsIntervention(l)) ? 'high' : 'medium',
      kind: 'engage',
      title: 'Re-engage learners who have gone quiet',
      rationale: `${quiet.length} learners have low watch completion or have not opened a module in over a week, and several of them are already behind.`,
      action: 'Send a personal nudge with the next module link, and pair each with a peer who has mastered it. Short check-ins now prevent these learners slipping further.',
      affected: quiet.map((l) => l.firstName),
      expectedImpact: 'Early re-engagement is the single biggest lever against end-of-term failure.',
    });
  }

  const order = { high: 0, medium: 1, low: 2 } as const;
  return actions.sort((a, b) => order[a.severity] - order[b.severity]);
}

export function studentActions(learner: Learner, roster: ClassRoster): NextAction[] {
  const actions: NextAction[] = [];
  const emerging = learner.conceptMastery
    .filter((c) => c.state === 'emerging')
    .map((c) => ({ m: c, concept: CONCEPT_BY_TAG.get(c.conceptTag)! }))
    // misconception-bearing gaps first, then by topic order
    .sort((a, b) => (b.m.lastMisconceptionTag ? 1 : 0) - (a.m.lastMisconceptionTag ? 1 : 0));

  for (const { m, concept } of emerging.slice(0, 2)) {
    const topic = TOPIC_BY_ID.get(concept.topicId)!;
    const hasMisc = !!m.lastMisconceptionTag;
    const miscLabel = hasMisc ? misconceptionLabel(m.lastMisconceptionTag!).replace(/\.$/, '').toLowerCase() : '';
    actions.push({
      id: `student-${learner.id}-${concept.tag}`,
      severity: hasMisc ? 'high' : 'medium',
      kind: 'reteach',
      title: `Close the gap on ${concept.label}`,
      rationale: hasMisc
        ? `${learner.firstName} scored ${m.correct}/${m.total} on ${concept.label} and repeatedly chose the same distractor, showing a clear misconception: ${miscLabel}.`
        : `${learner.firstName} scored ${m.correct}/${m.total} on ${concept.label}, the weakest concept so far.`,
      action: hasMisc
        ? `Reassign the "${topic.title}" module scene that addresses this misconception, then set 2 targeted ${concept.label} exercises and re-test in 3 days.`
        : `Set a short ${concept.label} practice set and a brief 1:1 check, then re-test.`,
      targetTopicId: topic.id,
      stepsToMastery: hasMisc ? 2 : 1,
      expectedImpact: hasMisc ? 'Naming the misconception directly is usually enough to unblock it within a week.' : 'A focused practice cycle should move this to Developing.',
    });
  }

  const mastered = learner.conceptMastery.filter((c) => c.state === 'mastered').length;
  if (emerging.length === 0 && mastered >= roster.concepts.length * 0.7) {
    actions.push({
      id: `student-${learner.id}-enrich`,
      severity: 'low',
      kind: 'enrich',
      title: 'Ready for a challenge',
      rationale: `${learner.firstName} has mastered ${mastered} of ${roster.concepts.length} concepts with no open gaps.`,
      action: 'Offer a cross-topic challenge problem or a peer-teaching role so the progress continues.',
      expectedImpact: 'Keeps a strong learner stretched and supports the rest of the class.',
    });
  }

  if (learner.engagementLevel === 'low') {
    actions.push({
      id: `student-${learner.id}-engage`,
      severity: needsIntervention(learner) ? 'high' : 'medium',
      kind: 'engage',
      title: 'Bring engagement back up',
      rationale: `${learner.firstName} is watching only part of each module (low completion) and was last active ${learner.lastActiveDaysAgo} days ago.`,
      action: 'A short personal nudge plus a peer pairing tends to restart momentum before the gap widens.',
      expectedImpact: 'Re-engaging now is far cheaper than remediating later.',
    });
  }

  const order = { high: 0, medium: 1, low: 2 } as const;
  return actions.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, 3);
}
